import { Icon } from '@iconify/react';
import GateActionDialog from './GateActionDialog';
import VerifyOtpDialog from './VerifyOtpDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import { cancelLeavesVisitorInside } from '../visitActions';

// Renders whichever transition dialog the `useVisitTransitions` hook currently
// has open. Both the list and the detail screen mount this once and hand it the
// hook, so the two screens can never drift apart on wording or guards.
const VisitTransitionDialogs = ({ tx, onCancelled }) => {
    const visit = tx.visit;
    if (!visit) return null;

    const who = visit.visitor?.name || visit.visitor_name || 'this visitor';
    const code = visit.visit_code ? `${visit.visit_code} · ` : '';

    return (
        <>
            <GateActionDialog
                open={tx.kind === 'reject'}
                title="Reject this visit?"
                subtitle={`${code}${who}`}
                fieldLabel="Reason"
                fieldHint="optional, shown to the gate"
                placeholder="e.g. Not expecting anyone"
                maxLength={255}
                multiline
                confirmLabel="Reject visit"
                busyLabel="Rejecting..."
                icon="mdi:close-octagon-outline"
                tone="tw-bg-rose-600 hover:tw-bg-rose-700"
                busy={tx.acting}
                onCancel={tx.close}
                onConfirm={tx.submitReject}
            />

            <GateActionDialog
                open={tx.kind === 'checkIn'}
                title="Check this visitor in?"
                subtitle={`${code}${who}`}
                fieldLabel="Entry gate"
                fieldHint="leave blank to keep the gate already on the visit"
                placeholder="e.g. Main Gate"
                maxLength={50}
                confirmLabel="Check in"
                busyLabel="Checking in..."
                icon="mdi:login"
                tone="tw-bg-sky-600 hover:tw-bg-sky-700"
                busy={tx.acting}
                initialValue={visit.entry_gate || ''}
                onCancel={tx.close}
                onConfirm={tx.submitCheckIn}
            />

            <GateActionDialog
                open={tx.kind === 'checkOut'}
                title="Check this visitor out?"
                subtitle={`${code}${who}`}
                fieldLabel="Exit gate"
                fieldHint="optional"
                placeholder="e.g. Main Gate"
                maxLength={50}
                confirmLabel="Check out"
                busyLabel="Checking out..."
                icon="mdi:logout"
                tone="tw-bg-orange-600 hover:tw-bg-orange-700"
                busy={tx.acting}
                initialValue={visit.exit_gate || visit.entry_gate || ''}
                onCancel={tx.close}
                onConfirm={tx.submitCheckOut}
            />

            {tx.kind === 'verifyOtp' && (
                <VerifyOtpDialog
                    visit={visit}
                    busy={tx.acting}
                    onCancel={tx.close}
                    onConfirm={tx.submitOtp}
                />
            )}

            <ConfirmDialog
                open={tx.kind === 'cancel'}
                title="Cancel this visit?"
                subtitle="The record is kept but marked cancelled."
                confirmLabel="Cancel visit"
                busyLabel="Cancelling..."
                tone="danger"
                icon="mdi:cancel"
                busy={tx.acting}
                onCancel={tx.close}
                onConfirm={() => tx.submitCancel(onCancelled)}
            >
                <p className="tw-mb-0">
                    <span className="tw-font-semibold">{who}</span>
                    {visit.visit_code ? ` (${visit.visit_code})` : ''} will be marked cancelled and removed from the list.
                </p>
                {/* The API cancels from any status, CHECKED_IN included — it will
                    close a visit for someone who is still inside, with no
                    check-out recorded and no warning of its own. */}
                {cancelLeavesVisitorInside(visit) && (
                    <div className="tw-flex tw-items-start tw-gap-2 tw-px-2.5 tw-py-2 tw-mt-3 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-100 tw-text-[10px] tw-text-amber-800">
                        <Icon icon="mdi:alert-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                        <span>
                            This visitor is currently <strong>checked in</strong>. Cancelling closes the visit with no
                            check-out recorded, so the gate log will not show them leaving. Check them out first unless
                            the entry was logged in error.
                        </span>
                    </div>
                )}
            </ConfirmDialog>
        </>
    );
};

export default VisitTransitionDialogs;
